import { useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { EllipsisHorizontal, Marketplace } from '@factorialco/f0-react/icons/app'

import {
  useModuleHeaderActions,
  type ModuleHeaderDropdownItem,
} from 'modules/layout/components/ResponsivePage/hooks'

import i18n from 'modules/core/lib/i18n'

import { useDeviceCatalogCartItemCount } from './useDeviceCatalogCartItemCount'

const DEVICE_CATALOG_BASE = '/device-catalog'
const ORDERS_PATH = `${DEVICE_CATALOG_BASE}/orders`

/**
 * Registers the device catalog cart button and "..." menu in the PageHeader.
 * Call from DeviceCatalogRootPage so the actions appear in the header while in the device catalog.
 * Uses f0 DropdownItem group labels for PROCUREMENT / BILLING / ADMINISTRATION (non-clickable headers).
 */
export function useDeviceCatalogHeaderActions(): void {
  const { setModuleHeaderActions } = useModuleHeaderActions()
  const { pathname } = useLocation()
  const cartItemCount = useDeviceCatalogCartItemCount()

  // Set actions when pathname or count change. useLayoutEffect runs before paint to avoid blink.
  // No cleanup - clearing here caused a blink when navigating list → details.
  useLayoutEffect(() => {
    const cartLabel =
      cartItemCount > 0
        ? i18n.t('it_management.device_catalog.cart.header_button_with_count', {
            count: cartItemCount,
          })
        : i18n.t('it_management.device_catalog.cart.header_button')

    const moreMenuItems: ModuleHeaderDropdownItem[] = [
      { type: 'label', text: i18n.t('it_management.device_catalog.menu_header_procurement') },
      { label: i18n.t('it_management.device_catalog.menu_orders'), href: ORDERS_PATH },
      { type: 'separator' },
      { type: 'label', text: i18n.t('it_management.device_catalog.menu_header_billing') },
      {
        type: 'label',
        text: i18n.t('it_management.device_catalog.menu_invoices_coming_soon'),
      },
      { type: 'separator' },
      { type: 'label', text: i18n.t('it_management.device_catalog.menu_header_administration') },
      {
        type: 'label',
        text: i18n.t('it_management.device_catalog.menu_settings_coming_soon'),
      },
    ]

    setModuleHeaderActions([
      {
        label: cartLabel,
        icon: Marketplace,
        href: `${pathname}#cart`,
        badge: cartItemCount > 0 ? cartItemCount : undefined,
      },
      {
        label: i18n.t('it_management.device_catalog.more_menu'),
        icon: EllipsisHorizontal,
        actions: moreMenuItems,
      },
    ])
  }, [pathname, cartItemCount, setModuleHeaderActions])

  // Clear only on unmount (when leaving device catalog)
  useEffect(() => {
    return () => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- temporary debug for cart blink investigation
        console.debug('[DeviceCatalogHeaderActions] unmount, clearing cart')
      }
      setModuleHeaderActions(null)
    }
  }, [setModuleHeaderActions])
}
